/**
 * ローマ字ガイド / 英語のタイプ文字列(docs/02_game_design.md §3)。
 *
 * 表示方式は難易度から `logic/guide.ts` が決める。
 * - full: 全文表示(打った分は色変化)
 * - typedOnly: 打った分のみ。残りは伏せ字にして「あと何打か」だけ伝える
 *
 * いま打つ位置(= 残りの先頭1文字)には下線を引く(参考画像の緑の下線)。
 * 下線は `.romaji-rest` / `.romaji-masked` の**中**に入れるので、
 * それらの textContent は「残り全部」のまま変わらない(e2e の打鍵はこれを読む)。
 *
 * 文章ステージ(日本語は最長70打鍵)は1行に入らないので `FitText` で縮小+2行折返しする。
 * サイズは「打った分 + 残り」の全長から決めるため、打っている途中に文字が飛び跳ねない。
 */

import type { Viewport } from '../../logic/textFit';
import { CursorText } from './CursorText';
import { FitText } from './FitText';

interface Props {
  readonly typed: string;
  readonly remaining: string;
  readonly mode: 'full' | 'typedOnly';
  readonly hintActive: boolean;
  readonly viewport: Viewport;
  /** いまの出題を見分ける値。変わったときだけ下線をワープさせる(CursorText の resetKey)。 */
  readonly wordKey: string;
}

export function RomajiGuide({ typed, remaining, mode, hintActive, viewport, wordKey }: Props) {
  return (
    <FitText
      role="romaji"
      // 伏せ字モードでも打鍵数は同じなので、全長でサイズを決めれば途中で変わらない。
      measureText={typed + remaining}
      viewport={viewport}
      className={`romaji ${hintActive ? 'hint' : ''}`}
      ariaLabel="ローマ字ガイド"
    >
      <span className="romaji-typed">{typed.replace(/ /g, '␣')}</span>
      {/*
        resetKey は出題そのもの。1打ごとに変わる text や「打った分 + 残り」を使うと
        (si/shi のような多重表記で全長が変わるので)毎回ワープ扱いになり下線が滑らない。
      */}
      {mode === 'full' ? (
        <span className="romaji-rest">
          <CursorText text={remaining} activeIndex={0} spaceChar="␣" resetKey={wordKey} />
        </span>
      ) : (
        <span className="romaji-masked">
          <CursorText text={'•'.repeat(remaining.length)} activeIndex={0} resetKey={wordKey} />
        </span>
      )}
    </FitText>
  );
}
